import "./Notifications.scss";

import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Alert from "ui/build/components/Alert/Alert";

import { t } from "../../../utils/ResourceUtils";
import NotificationPopOver from "./NotificationPopOver";
import { getNotificationList, getUnreadNotificationCount, markNotificationAsRead } from "./Notifications.service";

function Notifications({ pollingInterval }) {
  const [notificationData, setNotificationData] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alert, setAlert] = useState({
    alertType: "",
    message: "",
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pollingIntervalRef = useRef(null);

  const getUserId = () => {
    return JSON.parse(localStorage.getItem("loginResponse") || "")?.object?.username;
  };

  const fetchUnreadCount = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;

    const res = await getUnreadNotificationCount(userId);
    setUnreadCount(res?.data?.count ?? 0);
  }, []);

  const fetchNotifications = useCallback(async (page = 1, reset = false) => {
    try {
      const userId = getUserId();
      if (!userId) return;

      if (!reset) setIsLoadingMore(true);

      const res = await getNotificationList(userId, page, 10);
      if (res && res.data && res.data !== null) {
        const notifications = res.data.content || [];
        const processedNotifications = notifications.map((notification) => ({
          ...notification,
          status: notification.status === "UNREAD" ? "unDelivered" : "read",
          createdAt: moment.utc(notification.createdAt).local().format("dddd Do MMMM YYYY, h:mm a"),
        }));

        if (reset) {
          setNotificationData(processedNotifications);
        } else {
          setNotificationData((prevData) => [...prevData, ...processedNotifications]);
        }

        setHasNextPage(!res.data.last);
        setCurrentPage(page);
      }
    } catch (err) {
      setShowAlert(true);
      setAlert({
        alertType: "danger",
        message:
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : t("cm.error_while_fetching_notifications"),
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, []);

  const loadMoreNotifications = useCallback(() => {
    if (!isLoadingMore && hasNextPage) {
      fetchNotifications(currentPage + 1, false);
    }
  }, [isLoadingMore, hasNextPage, currentPage, fetchNotifications]);

  const handlePopoverToggle = useCallback(
    (isOpen) => {
      if (isOpen) {
        fetchNotifications(1, true);
      }
    },
    [fetchNotifications]
  );

  const callUpdate = (notify_id) => {
    markNotificationAsRead(notify_id)
      .then((res) => {
        if (res.status === 200) {
          setNotificationData((prevData) => {
            return prevData.map((notification) => {
              if (notification.notificationId === notify_id) {
                return { ...notification, status: "read" };
              }
              return notification;
            });
          });
          setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
        } else {
          setShowAlert(true);
          setAlert({
            alertType: "danger",
            message: t("cm.error_while_updating_notifications"),
          });
        }
      })
      .catch((err) => {
        setShowAlert(true);
        setAlert({
          alertType: "danger",
          message:
            err && err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : t("cm.error_while_updating_notifications"),
        });
      });
  };

  useEffect(() => {
    fetchUnreadCount();

    pollingIntervalRef.current = setInterval(() => {
      fetchUnreadCount();
    }, pollingInterval);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchUnreadCount, pollingInterval]);

  return (
    <div className="notification-container">
      {showAlert ? (
        <Alert
          alertType={alert.alertType}
          show={showAlert}
          dismiss={true}
          onClose={() => {
            setShowAlert(false);
          }}
        >
          {alert.message}
        </Alert>
      ) : null}
      <NotificationPopOver
        notificationData={notificationData}
        unreadCount={unreadCount}
        callUpdate={callUpdate}
        onToggle={handlePopoverToggle}
        onLoadMore={loadMoreNotifications}
        isLoadingMore={isLoadingMore}
        hasNextPage={hasNextPage}
      />
    </div>
  );
}

export default Notifications;
